import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { numberLocaleFor } from './numberLocale'

// Import translation files using the new modular approach
import translations from '../locales'
// Import specific files for namespaces
import servicesEn from '../locales/en/services.json'
import servicesAr from '../locales/ar/services.json'
import sdgEn from '../locales/en/sdg.json'
import sdgAr from '../locales/ar/sdg.json'
import commonEn from '../locales/en/common.json'
import commonAr from '../locales/ar/common.json'
import filtersEn from '../locales/en/filters.json'
import filtersAr from '../locales/ar/filters.json'

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        translation: translations.en,
        common: commonEn,
        services: servicesEn,
        sdg: sdgEn,
        filters: filtersEn,
        homepage: translations.en.homepage,
        contact: translations.en.contact,
        cart: translations.en.cart,
        equipment: translations.en.equipment,
        about: translations.en.about,
        hero: translations.en.hero,
        inquiry: translations.en.inquiry,
        auth: translations.en.auth,
        dashboard: translations.en.dashboard
      },
      ar: {
        translation: translations.ar,
        common: commonAr,
        sdg: sdgAr,
        services: servicesAr,
        filters: filtersAr,
        homepage: translations.ar.homepage,
        contact: translations.ar.contact,
        cart: translations.ar.cart,
        equipment: translations.ar.equipment,
        about: translations.ar.about,
        hero: translations.ar.hero,
        inquiry: translations.ar.inquiry,
        auth: translations.ar.auth,
        dashboard: translations.ar.dashboard
      }
    },
    lng: 'en', // default language
    fallbackLng: 'en',
    defaultNS: 'translation',
    ns: ['translation', 'common', 'services', 'filters', 'homepage', 'contact', 'cart', 'equipment', 'about', 'hero', 'inquiry', 'auth', 'dashboard'],
    debug: process.env.NODE_ENV === 'development',
    
    interpolation: {
      escapeValue: false // React already escapes values
    },
    
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage']
    }
  })

/**
 * Make `{{value, number}}` print Arabic-Indic digits in Arabic.
 *
 * i18next's built-in `number` formatter calls Intl.NumberFormat with the
 * ACTIVE LANGUAGE TAG, and this app's tag is the bare 'ar' - for which ICU
 * resolves the `latn` numbering system, i.e. plain "3". Only a region-qualified
 * tag resolves to `arab`:
 *
 *   new Intl.NumberFormat('ar').resolvedOptions().numberingSystem     -> 'latn'
 *   new Intl.NumberFormat('ar-EG').resolvedOptions().numberingSystem  -> 'arab'
 *
 * That mismatch is why a document list read "٩ أغسطس ٢٠٢٦ · 3 بنود" - the date
 * came from lib/cairoDate.ts, which already pins 'ar-EG', while the count came
 * from here and did not. Two numbering systems in one sentence.
 *
 * Overriding the formatter rather than renaming the language keeps every
 * translation key ('ar') and every locale file untouched.
 *
 * The map itself (`NUMBER_LOCALES`) lives in `lib/numberLocale.ts`, not here -
 * see that module's header for why: it lets a pure, offline-loadable module
 * resolve the identical locale without importing this file's `i18n.init()`
 * side effect, which touches browser globals this app's own verifiers do not
 * have.
 */
i18n.services.formatter?.add('number', (value, lng) => {
  const locale = numberLocaleFor(lng || '')
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return String(value)
  return new Intl.NumberFormat(locale).format(numeric)
})

/**
 * Format a bare number for display, with the same ar-EG pinning the i18next
 * `number` formatter above applies. Use this for a figure that is NOT inside a
 * translated sentence - passing an interpolation template to t() as though it
 * were a key happens to work (a missing key falls back to itself and is then
 * interpolated) but logs missingKey on every render and depends on fallback
 * behaviour rather than on anything guaranteed.
 */
export const formatNumber = (value: number, lng?: string): string => {
  const language = lng || i18n.language || 'en'
  const locale = numberLocaleFor(language)
  return Number.isFinite(value) ? new Intl.NumberFormat(locale).format(value) : String(value)
}

export default i18n
